import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Motif } from '../../../types/motifs'
import { useContextSelectedMotifs } from '../../../contexts/selectedMotifContext'
import { EXPORT_SIZE, useContextMotifGenerator } from '../../../contexts/motifGeneratorContext'
import { initializeAroundMotifs } from './initAroundMotifs'
import { isAndroid, isIOS } from 'react-device-detect'
import { PositionXY } from '../../../types/PositionXY'
import { getPagePositionXY } from '../../../utils/getPagePositionXY'
import { useThrottleFn } from '@react-cmpt/use-throttle'
import { JSONExportPlane, MotifGeneratorType } from '../types'
import { getTransformProperties } from '../util'
import { ExportLayoutModalResponse } from '../ExportLayoutModal'
import { isSafari, SAFARI_MAX_CANVAS_SIZE } from '../../../utils/safariCanvasUtils'

const TILE_NUMBER = 9
const TILES_MULTIPLIER = 3
const AROUND_MOTIF_SPACING = 1
const AMOUNT_OF_MOTIFS_AROUND = 16
const RESOLUTION_MULTIPLIER = 2
const EXPORT_RESOLUTION_MULTIPLIER = 2

const Container = styled.div`
  width: 120vw;
  height: 120vw;
  display: flex;
  flex-wrap: wrap;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: #fff;
    z-index: 10;
  }

  .tile {
    background-color: ${({ theme }) => theme.colors.white};
    width: calc(100% / 3);
    height: calc(100% / 3);
    position: relative;
    overflow: hidden;
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
    z-index: 20;

    &.tile-to-export {
      width: ${EXPORT_SIZE}px;
      height: ${EXPORT_SIZE}px;
      position: absolute;
      left: 0;
      top: 0;
      z-index: 1;
    }

    canvas {
      width: 100%;
      height: 100%;
    }
  }
`
type Props = {
  rotationAngleProp?: number
  exportResponse?: ExportLayoutModalResponse
}

const CircleLayoutGeneratorCanvas = ({ rotationAngleProp, exportResponse }: Props) => {
  const { selectedMotifs } = useContextSelectedMotifs()
  const [firstTimeGenerated, setFirstTimeGenerated] = useState<Boolean>(false)
  const [centerMotif, setCenterMotif] = useState<Motif>()
  const [aroundMotifs, setAroundMotifs] = useState<Motif[]>([])
  const canvasRefs = useRef<HTMLCanvasElement[]>([])
  const canvasToExportRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [images, setImages] = useState<HTMLImageElement[]>([])
  const [imageLoadingIsFinished, setImageLoadingIsFinished] = useState<boolean>(false)
  const [canvasDataUrl, setCanvasDataUrl] = useState<string | null>(null)
  const lastPos = useRef<PositionXY>({ x: 0, y: 0 })
  const dragging = useRef<boolean>(false)
  const defaultRotationAngle = 0
  const [rotationAngle, setRotationAngle] = useState(rotationAngleProp || defaultRotationAngle)
  const jsonCoordinates = useRef<JSONExportPlane>({
    clipPlane: undefined,
    entries: [],
  })

  const {
    exportInvoked,
    exportModal,
    startExport,
    setStartExport,
    exportGeneratorCoordinatesToJSON,
    handleExport,
    handleExportCanvas,
    isPatternGenerated,
    setIsPatternGenerated,
  } = useContextMotifGenerator()
  const keepExportCanvas = useRef<HTMLCanvasElement | null>(null)

  const loadImages = (index: number) => {
    const img = new Image()
    img.crossOrigin = ''
    const motif = selectedMotifs[index]
    img.src = motif.originalSVGUrl || motif.vectorizedUrl!
    img.onload = () => {
      setImages([...images, img])
    }
  }

  const sortImages = (imagesToSort: HTMLImageElement[], sortByArray: string[]) => {
    return [...imagesToSort].sort((a, b) => sortByArray.indexOf(a.src) - sortByArray.indexOf(b.src))
  }

  useEffect(() => {
    if (images.length === selectedMotifs.length) {
      setImageLoadingIsFinished(true)

      if (imageLoadingIsFinished) {
        setImages(
          sortImages(
            images,
            selectedMotifs.map(motif => motif.originalSVGUrl || motif.vectorizedUrl!),
          ),
        )
      }
    }
    if (images.length < selectedMotifs.length) {
      loadImages(images.length)
    }
    if (images.length > selectedMotifs.length) {
      setImages(
        images.filter(img =>
          selectedMotifs
            .map(motif => motif.originalSVGUrl || motif.vectorizedUrl)
            .includes(img.src),
        ),
      )
    }
  }, [selectedMotifs, images.length, imageLoadingIsFinished])

  // Prevents pull-to refresh behavior on iOS devices
  useEffect(() => {
    if (!containerRef.current) return
    let prevent = false

    containerRef.current.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) return

      const scrollY =
        window.pageYOffset || document.body.scrollTop || document.documentElement.scrollTop
      prevent = scrollY === 0
    })

    containerRef.current.addEventListener('touchmove', function (e) {
      if (prevent) {
        prevent = false
        e.preventDefault()
      }
    })
  }, [containerRef.current])

  useEffect(() => {
    if (!exportInvoked || !canvasToExportRef.current || !exportModal) return
    // Clamp export canvas size for Safari
    if (isSafari) {
      const maxSize = SAFARI_MAX_CANVAS_SIZE
      if (canvasToExportRef.current.width > maxSize) canvasToExportRef.current.width = maxSize
      if (canvasToExportRef.current.height > maxSize) canvasToExportRef.current.height = maxSize
    }
    keepExportCanvas.current = canvasToExportRef.current
    setCanvasDataUrl(canvasToExportRef.current.toDataURL())
  }, [exportInvoked, canvasToExportRef])

  useEffect(() => {
    if (!startExport || !keepExportCanvas.current) return

    if (exportResponse === ExportLayoutModalResponse.TILES) {
      create3By3tiles()
        .then(resolvedCanvas => {
          keepExportCanvas.current = resolvedCanvas as HTMLCanvasElement
          exportCanvas()
        })
        .catch(error => {
          console.error('Error creating tiled base64:', error)
        })
    } else {
      exportCanvas()
    }
    setStartExport(false)
  }, [startExport, exportResponse])

  const exportCanvas = async () => {
    if (!keepExportCanvas.current) return
    // Clamp export canvas size for Safari
    if (isSafari) {
      const maxSize = SAFARI_MAX_CANVAS_SIZE
      if (keepExportCanvas.current.width > maxSize) keepExportCanvas.current.width = maxSize
      if (keepExportCanvas.current.height > maxSize) keepExportCanvas.current.height = maxSize
    }
    setCanvasDataUrl(keepExportCanvas.current.toDataURL())

    exportGeneratorCoordinatesToJSON({
      props: {
        type: MotifGeneratorType.SQUARE,
        selectedMotifs,
        rotationAngle,
      },
      planes: [jsonCoordinates.current],
      canvasSize: {
        width: canvasRefs.current[0].width,
        height: canvasRefs.current[0].height,
      },
    })
    if (isIOS || isAndroid) {
      handleExportCanvas(keepExportCanvas.current!)
    } else {
      const exportCanvasToImage = async () => {
        await handleExport(keepExportCanvas.current!)
      }
      exportCanvasToImage().catch(console.error)
    }
    setStartExport(false)
    setCanvasDataUrl(null)
  }

  const create3By3tiles = () => {
    const tilesX = TILES_MULTIPLIER
    const tilesY = TILES_MULTIPLIER
    // PC: 9x 3000x3000 -> 9000x9000 -> 3000x3000
    // Safari: 9x 1000x1000 -> 3000x3000
    return new Promise((resolve, reject) => {
      const tileCanvases: HTMLCanvasElement[] = []
      const tileSize = isSafari ? 1000 : 3000
      const exportSize = isSafari ? 3000 : 9000
      // 1. Create tile canvases and copy the original canvas content scaled
      for (let i = 0; i < tilesX * tilesY; i++) {
        const tile = document.createElement('canvas')
        tile.width = tileSize
        tile.height = tileSize
        const ctx = tile.getContext('2d')
        if (ctx && keepExportCanvas.current) {
          // Scale the original canvas content to the tile size
          ctx.drawImage(
            keepExportCanvas.current,
            0,
            0,
            keepExportCanvas.current.width,
            keepExportCanvas.current.height,
            0,
            0,
            tileSize,
            tileSize,
          )
        }
        tileCanvases.push(tile)
      }
      // 2. Merge canvas tiles to a 3x3 matrix on a single canvas
      const exportCanvas = document.createElement('canvas')
      exportCanvas.width = exportSize
      exportCanvas.height = exportSize
      const exportCtx = exportCanvas.getContext('2d')
      for (let y = 0; y < tilesY; y++) {
        for (let x = 0; x < tilesX; x++) {
          const idx = y * tilesX + x
          if (exportCtx) {
            exportCtx.drawImage(
              tileCanvases[idx],
              0,
              0,
              tileSize,
              tileSize,
              x * tileSize,
              y * tileSize,
              tileSize,
              tileSize,
            )
          }
        }
      }
      // 3. scaling 9000x9000 -> 3000x3000, in safari it is already 3000x3000
      if (!isSafari) {
        const finalCanvas = document.createElement('canvas')
        finalCanvas.width = 3000
        finalCanvas.height = 3000
        const finalCtx = finalCanvas.getContext('2d')
        if (finalCtx) {
          finalCtx.drawImage(
            exportCanvas,
            0,
            0,
            exportCanvas.width,
            exportCanvas.height,
            0,
            0,
            3000,
            3000,
          )
        }
        exportCanvas.remove()
        tileCanvases.forEach(c => c.remove())
        resolve(finalCanvas)
      } else {
        tileCanvases.forEach(c => c.remove())
        resolve(exportCanvas)
      }
    })
  }

  useEffect(() => {
    if (!selectedMotifs) return
    if (!isPatternGenerated) setIsPatternGenerated(true)

    setCenterMotif(selectedMotifs[0])
    if (selectedMotifs.length === 1) {
      setAroundMotifs([selectedMotifs[0]])
      return
    }
    setAroundMotifs(selectedMotifs.filter((_m, i) => i !== 0))
  }, [selectedMotifs])

  useEffect(() => {
    if (exportInvoked && canvasToExportRef.current) {
      draw(canvasToExportRef.current, EXPORT_RESOLUTION_MULTIPLIER)
    }

    if (canvasRefs.current.length !== TILE_NUMBER || selectedMotifs.length !== images.length) return

    canvasRefs.current.forEach(canvas => {
      draw(canvas, RESOLUTION_MULTIPLIER)
    })

    if (!firstTimeGenerated) {
      setFirstTimeGenerated(true)
    }
  }, [canvasRefs, aroundMotifs, centerMotif, images, canvasDataUrl, rotationAngle, exportInvoked])

  const draw = (
    canvas: HTMLCanvasElement,
    resolutionMultiplier: number = RESOLUTION_MULTIPLIER,
  ) => {
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    canvas.width = canvas.offsetWidth * resolutionMultiplier
    canvas.height = canvas.offsetHeight * resolutionMultiplier
    if (!ctx) return

    jsonCoordinates.current.entries = []

    ctx.scale(resolutionMultiplier, resolutionMultiplier)
    ctx.imageSmoothingQuality = 'high'
    ctx.imageSmoothingEnabled = true

    ctx.beginPath()
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    const centralImageWidth =
      canvas.offsetWidth * 0.8 - selectedMotifs.length * (canvas.offsetWidth * 0.13)
    const centralImageHeight =
      canvas.offsetHeight * 0.8 - selectedMotifs.length * (canvas.offsetHeight * 0.13)

    const centralImageX = canvas.offsetWidth / 2 - centralImageWidth / 2
    const centralImageY = canvas.offsetHeight / 2 - centralImageHeight / 2

    ctx.save()
    ctx.translate(centralImageX + centralImageWidth / 2, centralImageY + centralImageHeight / 2)
    ctx.rotate(rotationAngle)

    if (images.length === 0) return
    ctx.drawImage(
      images[0],
      -centralImageWidth / 2,
      -centralImageHeight / 2,
      centralImageWidth,
      centralImageHeight,
    )
    const { origin, rotation, scale } = getTransformProperties(ctx)
    ctx.restore()

    jsonCoordinates.current.entries.push({
      id: selectedMotifs[0].id,
      x: origin.x,
      y: origin.y,
      size: centralImageWidth * scale,
      rotation: rotation,
    })

    const OUTER_MOTIFS = { size: canvas.offsetWidth * 0.15, Y: 0 }
    const MIDDLE_MOTIFS = {
      size: canvas.offsetWidth * 0.12,
      Y: OUTER_MOTIFS.size + AROUND_MOTIF_SPACING,
    }
    const INNER_MOTIFS = {
      size: canvas.offsetWidth * 0.086,
      Y: OUTER_MOTIFS.size + MIDDLE_MOTIFS.size + 2 * AROUND_MOTIF_SPACING,
    }

    switch (selectedMotifs.length) {
      case 2:
        jsonCoordinates.current.entries.push(
          ...initializeAroundMotifs(
            OUTER_MOTIFS.size,
            OUTER_MOTIFS.Y,
            rotationAngle * 2,
            selectedMotifs[1].id,
            images[1] || '',
            ctx,
            canvas,
            AMOUNT_OF_MOTIFS_AROUND,
          ),
        )
        break
      case 3:
        jsonCoordinates.current.entries.push(
          ...initializeAroundMotifs(
            OUTER_MOTIFS.size,
            OUTER_MOTIFS.Y,
            rotationAngle * 2,
            selectedMotifs[2].id,
            images[2],
            ctx,
            canvas,
            AMOUNT_OF_MOTIFS_AROUND,
          ),
        )
        jsonCoordinates.current.entries.push(
          ...initializeAroundMotifs(
            MIDDLE_MOTIFS.size,
            MIDDLE_MOTIFS.Y,
            rotationAngle * 2,
            selectedMotifs[1].id,
            images[1],
            ctx,
            canvas,
            AMOUNT_OF_MOTIFS_AROUND,
          ),
        )
        break
      case 4:
        jsonCoordinates.current.entries.push(
          ...initializeAroundMotifs(
            OUTER_MOTIFS.size,
            OUTER_MOTIFS.Y,
            rotationAngle * 2,
            selectedMotifs[3].id,
            images[3],
            ctx,
            canvas,
            AMOUNT_OF_MOTIFS_AROUND,
          ),
        )
        jsonCoordinates.current.entries.push(
          ...initializeAroundMotifs(
            MIDDLE_MOTIFS.size,
            MIDDLE_MOTIFS.Y,
            rotationAngle * 2,
            selectedMotifs[2].id,
            images[2],
            ctx,
            canvas,
            AMOUNT_OF_MOTIFS_AROUND,
          ),
        )
        jsonCoordinates.current.entries.push(
          ...initializeAroundMotifs(
            INNER_MOTIFS.size,
            INNER_MOTIFS.Y,
            rotationAngle * 2,
            selectedMotifs[1].id,
            images[1],
            ctx,
            canvas,
            AMOUNT_OF_MOTIFS_AROUND,
          ),
        )
        break
      default:
        jsonCoordinates.current.entries.push(
          ...initializeAroundMotifs(
            OUTER_MOTIFS.size,
            OUTER_MOTIFS.Y,
            rotationAngle * 2,
            selectedMotifs[0].id,
            images[0],
            ctx,
            canvas,
            AMOUNT_OF_MOTIFS_AROUND,
          ),
        )
    }

    jsonCoordinates.current.clipPlane = undefined
  }

  useEffect(() => {
    if (!firstTimeGenerated) return
    setCanvasDataUrl(canvasRefs.current[0].toDataURL())
  }, [firstTimeGenerated])

  const handleMouseDown = (event: React.MouseEvent) => {
    event.preventDefault()
    onStartEvent(event.pageX, event.pageY)
  }

  const handleTouchStart = (event: React.TouchEvent) => {
    onStartEvent(event.touches[0].pageX, event.touches[0].pageY)
  }

  const onStartEvent = (x: number, y: number) => {
    lastPos.current.x = x
    lastPos.current.y = y
    dragging.current = true
  }

  const handleMouseUp = (event: React.MouseEvent | React.TouchEvent) => {
    event.preventDefault()
    dragging.current = false
  }

  const handleMouseMove = (event: React.MouseEvent | React.TouchEvent): void => {
    if (dragging.current) {
      const pageXY = getPagePositionXY(event)
      const moveX = lastPos.current.x - pageXY.x
      lastPos.current = pageXY
      setRotationAngle(rotationAngle + moveX / 100)
    }
  }

  const { callback } = useThrottleFn(handleMouseMove, 40)

  return (
    <div
      style={{
        position: 'fixed',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
      }}
    >
      <Container
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={callback}
        onMouseUp={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={callback}
        onTouchEnd={handleMouseUp}
      >
        {[...Array(TILE_NUMBER)].map((_, i) => {
          return (
            <div className="tile" key={i}>
              <canvas id="canvas-{i}" ref={canvas => (canvasRefs.current[i] = canvas!)}></canvas>;
            </div>
          )
        })}
        {exportModal ? (
          <div className="tile tile tile-to-export">
            <canvas
              className="canvas-to-export"
              width={EXPORT_SIZE / EXPORT_RESOLUTION_MULTIPLIER}
              height={EXPORT_SIZE / EXPORT_RESOLUTION_MULTIPLIER}
              ref={canvasToExportRef}
            ></canvas>
          </div>
        ) : null}
      </Container>
    </div>
  )
}

export default CircleLayoutGeneratorCanvas
